import { defineStore } from "pinia";
import { ref } from "vue";
import type {
  Inverter,
  Panel,
  Battery,
  EVCharger,
  Meter,
  Kit,
  Service,
  InstallmentRatio,
  CoefficientBasedDirection,
} from "@/types/projectTypes";
import api from "@/plugins/api";

export const useEquipmentStore = defineStore("equipment", () => {
  const inverters = ref<Inverter[]>([]);
  const panels = ref<Panel[]>([]);
  const batteries = ref<Battery[]>([]);
  const evChargers = ref<EVCharger[]>([]);
  const meters = ref<Meter[]>([]);
  const kits = ref<Kit[]>([]);
  const services = ref<Service[]>([]);
  const installmentRatios = ref<InstallmentRatio[]>([]);
  const coefficientDirections = ref<CoefficientBasedDirection[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // API service functions moved here
  const fetchInverters = async (): Promise<Inverter[]> => {
    try {
      console.log("Fetching inverters from API...");
      const response = await api.get("/inverters/");
      console.log("Raw inverters API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed inverters data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch inverters:", error);
      return [];
    }
  };

  const fetchPanels = async (): Promise<Panel[]> => {
    try {
      console.log("Fetching panels from API...");
      const response = await api.get("/panels/");
      console.log("Raw panels API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed panels data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch panels:", error);
      return [];
    }
  };

  const fetchBatteries = async (): Promise<Battery[]> => {
    try {
      console.log("Fetching batteries from API...");
      const response = await api.get("/batteries/");
      console.log("Raw batteries API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed batteries data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch batteries:", error);
      return [];
    }
  };

  const fetchEVChargers = async (): Promise<EVCharger[]> => {
    try {
      console.log("Fetching EV chargers from API...");
      const response = await api.get("/ev-chargers/");
      console.log("Raw EV chargers API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed EV chargers data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch EV chargers:", error);
      return [];
    }
  };

  const fetchMeters = async (): Promise<Meter[]> => {
    try {
      console.log("Fetching meters from API...");
      const response = await api.get("/meters/");
      console.log("Raw meters API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed meters data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch meters:", error);
      return [];
    }
  };

  const fetchKits = async (): Promise<Kit[]> => {
    try {
      console.log("Fetching kits from API...");
      const response = await api.get("/kits/");
      console.log("Raw kits API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed kits data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch kits:", error);
      return [];
    }
  };

  const fetchServices = async (): Promise<Service[]> => {
    try {
      console.log("Fetching services from API...");
      const response = await api.get("/services/");
      console.log("Raw services API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed services data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch services:", error);
      return [];
    }
  };

  const fetchInstallmentRatios = async (): Promise<InstallmentRatio[]> => {
    try {
      console.log("Fetching installment ratios from API...");
      const response = await api.get("/installment-ratios/");
      console.log("Raw installment ratios API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed installment ratios data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch installment ratios:", error);
      return [];
    }
  };

  const fetchCoefficientDirections = async (): Promise<
    CoefficientBasedDirection[]
  > => {
    try {
      console.log("Fetching coefficient directions from API...");
      const response = await api.get("/coefficient-directions/");
      console.log("Raw coefficient directions API response:", response);
      const data = response.data.results || response.data;
      console.log("Processed coefficient directions data:", data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch coefficient directions:", error);
      return [];
    }
  };

  const fetchEquipment = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      console.log("Starting to fetch all equipment data...");

      const [
        inverterData,
        panelData,
        batteryData,
        evChargerData,
        meterData,
        kitData,
        serviceData,
        installmentRatiosData,
        coefficientDirectionsData,
      ] = await Promise.all([
        fetchInverters(),
        fetchPanels(),
        fetchBatteries(),
        fetchEVChargers(),
        fetchMeters(),
        fetchKits(),
        fetchServices(),
        fetchInstallmentRatios(),
        fetchCoefficientDirections(),
      ]);

      console.log("Fetched inverters:", inverterData);
      console.log("Fetched panels:", panelData);
      console.log("Fetched batteries:", batteryData);
      console.log("Fetched EV chargers:", evChargerData);
      console.log("Fetched meters:", meterData);
      console.log("Fetched kits:", kitData);
      console.log("Fetched services:", serviceData);
      console.log("Fetched installment ratios:", installmentRatiosData);
      console.log("Fetched coefficient directions:", coefficientDirectionsData);

      if (Array.isArray(inverterData)) {
        inverters.value = inverterData;
      } else {
        throw new Error("Invalid inverter data format");
      }

      if (Array.isArray(panelData)) {
        panels.value = panelData;
      } else {
        throw new Error("Invalid panel data format");
      }

      if (Array.isArray(batteryData)) {
        batteries.value = batteryData;
      } else {
        console.warn(
          "No battery data found or invalid format, using empty array"
        );
        batteries.value = [];
      }

      if (Array.isArray(evChargerData)) {
        evChargers.value = evChargerData;
      } else {
        console.warn(
          "No EV charger data found or invalid format, using empty array"
        );
        evChargers.value = [];
      }

      if (Array.isArray(meterData)) {
        meters.value = meterData;
      } else {
        console.warn(
          "No meter data found or invalid format, using empty array"
        );
        meters.value = [];
      }

      if (Array.isArray(kitData)) {
        kits.value = kitData;
      } else {
        console.warn("No kit data found or invalid format, using empty array");
        kits.value = [];
      }

      if (Array.isArray(serviceData)) {
        services.value = serviceData;
      } else {
        console.warn(
          "No service data found or invalid format, using empty array"
        );
        services.value = [];
      }

      if (Array.isArray(installmentRatiosData)) {
        installmentRatios.value = installmentRatiosData;
      } else {
        console.warn(
          "No installment ratios data found or invalid format, using empty array"
        );
        installmentRatios.value = [];
      }

      if (Array.isArray(coefficientDirectionsData)) {
        coefficientDirections.value = coefficientDirectionsData;
      } else {
        console.warn(
          "No coefficient directions data found or invalid format, using empty array"
        );
        coefficientDirections.value = [];
      }
    } catch (err) {
      console.error("Failed to fetch equipment:", err);
      error.value = "Failed to load equipment data. Please try again.";
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Computed getters for unique manufacturers
  const getInverterBrands = () => {
    return [...new Set(inverters.value.map((inv) => inv.manufacturer))].sort();
  };

  const getPanelBrands = () => {
    return [...new Set(panels.value.map((panel) => panel.manufacturer))].sort();
  };

  const getBatteryBrands = () => {
    return [
      ...new Set(batteries.value.map((battery) => battery.manufacturer)),
    ].sort();
  };

  const getEVChargerBrands = () => {
    return [
      ...new Set(evChargers.value.map((charger) => charger.manufacturer)),
    ].sort();
  };

  const getMeterBrands = () => {
    return [...new Set(meters.value.map((meter) => meter.manufacturer))].sort();
  };

  // Methods to get models by manufacturer
  const getInverterModels = (manufacturer: string) => {
    if (!manufacturer) return [];
    return inverters.value
      .filter((inv) => inv.manufacturer === manufacturer)
      .map((inv) => inv.model_number)
      .sort();
  };

  const getPanelModels = (manufacturer: string) => {
    if (!manufacturer) return [];
    return panels.value
      .filter((panel) => panel.manufacturer === manufacturer)
      .map((panel) => panel.model_number)
      .sort();
  };

  const getBatteryModels = (manufacturer: string) => {
    if (!manufacturer) return [];
    return batteries.value
      .filter((battery) => battery.manufacturer === manufacturer)
      .map((battery) => battery.model_number)
      .sort();
  };

  const getEVChargerModels = (manufacturer: string) => {
    if (!manufacturer) return [];
    return evChargers.value
      .filter((charger) => charger.manufacturer === manufacturer)
      .map((charger) => charger.model_number)
      .sort();
  };

  const getMeterModels = (manufacturer: string) => {
    if (!manufacturer) return [];
    return meters.value
      .filter((meter) => meter.manufacturer === manufacturer)
      .map((meter) => meter.model_number)
      .sort();
  };

  // Helper function to get kit by ID
  const getKitById = (id: string) => {
    return kits.value.find((kit) => kit.id === id);
  };

  return {
    inverters,
    panels,
    batteries,
    evChargers,
    meters,
    kits,
    services,
    installmentRatios,
    coefficientDirections,
    isLoading,
    error,
    fetchEquipment,
    getInverterBrands,
    getPanelBrands,
    getBatteryBrands,
    getEVChargerBrands,
    getMeterBrands,
    getInverterModels,
    getPanelModels,
    getBatteryModels,
    getEVChargerModels,
    getMeterModels,
    getKitById,
  };
});
